<script setup lang="ts">
import type { Component } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { ChevronDown, ChevronRight } from 'lucide-vue-next';
import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';

type ResponsiveActionSubmenuItem = {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: Component;
  color?: string;
};

const props = withDefaults(defineProps<{
  mode: 'dropdown' | 'context';
  label: string;
  items: ResponsiveActionSubmenuItem[];
  icon?: Component;
  disabled?: boolean;
  triggerMeta?: string;
  emptyLabel?: string;
  contentClass?: string;
  inline?: boolean;
}>(), {
  icon: undefined,
  disabled: false,
  triggerMeta: '',
  emptyLabel: 'No options available.',
  contentClass: 'w-52',
  inline: false,
});

const emit = defineEmits<{
  select: [item: ResponsiveActionSubmenuItem];
}>();

const isMobile = useMediaQuery('(max-width: 767px)');
const expanded = ref(false);

const menuItem = computed(() => props.mode === 'context' ? ContextMenuItem : DropdownMenuItem);

watch(isMobile, () => {
  expanded.value = false;
});

function toggleMobile(event: Event) {
  event.preventDefault();
  if (props.disabled) return;
  expanded.value = !expanded.value;
}

function selectItem(item: ResponsiveActionSubmenuItem) {
  if (item.disabled) return;
  emit('select', item);
  expanded.value = false;
}
</script>

<template>
  <template v-if="isMobile || inline">
    <component
      :is="menuItem"
      class="gap-2"
      :disabled="disabled"
      @select="toggleMobile"
    >
      <component :is="icon" v-if="icon" class="size-4 shrink-0" />
      <span class="min-w-0 flex-1 truncate">{{ label }}</span>
      <span v-if="triggerMeta" class="text-xs text-muted-foreground">{{ triggerMeta }}</span>
      <ChevronDown
        class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
        :class="expanded ? 'rotate-180' : ''"
      />
    </component>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="-translate-y-1 opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="-translate-y-1 opacity-0"
    >
      <div v-if="expanded" class="mx-1 mb-1 space-y-0.5 border-l border-border/70 py-1 pl-2">
        <component
          :is="menuItem"
          v-for="item in items"
          :key="item.id"
          class="gap-2"
          :disabled="item.disabled"
          @select="selectItem(item)"
        >
          <component :is="item.icon" v-if="item.icon" class="size-4 shrink-0" />
          <span
            v-else-if="item.color"
            class="size-2.5 shrink-0 rounded-full"
            :style="{ backgroundColor: item.color }"
          />
          <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
          <span v-if="item.description" class="shrink-0 text-xs text-muted-foreground">
            {{ item.description }}
          </span>
        </component>
        <p v-if="items.length === 0" class="px-2 py-2 text-xs text-muted-foreground">
          {{ emptyLabel }}
        </p>
      </div>
    </Transition>
  </template>

  <DropdownMenuSub v-else-if="mode === 'dropdown'">
    <DropdownMenuSubTrigger class="gap-2" :disabled="disabled">
      <component :is="icon" v-if="icon" class="size-4 shrink-0" />
      <span class="min-w-0 flex-1 truncate">{{ label }}</span>
      <span v-if="triggerMeta" class="mr-1 text-xs text-muted-foreground">{{ triggerMeta }}</span>
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent :class="contentClass">
      <DropdownMenuItem
        v-for="item in items"
        :key="item.id"
        class="gap-2"
        :disabled="item.disabled"
        @select="selectItem(item)"
      >
        <component :is="item.icon" v-if="item.icon" class="size-4 shrink-0" />
        <span
          v-else-if="item.color"
          class="size-2.5 shrink-0 rounded-full"
          :style="{ backgroundColor: item.color }"
        />
        <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
        <span v-if="item.description" class="text-xs text-muted-foreground">{{ item.description }}</span>
      </DropdownMenuItem>
      <p v-if="items.length === 0" class="px-2 py-2 text-xs text-muted-foreground">{{ emptyLabel }}</p>
    </DropdownMenuSubContent>
  </DropdownMenuSub>

  <ContextMenuSub v-else>
    <ContextMenuSubTrigger class="gap-2" :disabled="disabled">
      <component :is="icon" v-if="icon" class="size-4 shrink-0" />
      <span class="min-w-0 flex-1 truncate">{{ label }}</span>
      <span v-if="triggerMeta" class="mr-1 text-xs text-muted-foreground">{{ triggerMeta }}</span>
    </ContextMenuSubTrigger>
    <ContextMenuSubContent :class="contentClass">
      <ContextMenuItem
        v-for="item in items"
        :key="item.id"
        class="gap-2"
        :disabled="item.disabled"
        @select="selectItem(item)"
      >
        <component :is="item.icon" v-if="item.icon" class="size-4 shrink-0" />
        <span
          v-else-if="item.color"
          class="size-2.5 shrink-0 rounded-full"
          :style="{ backgroundColor: item.color }"
        />
        <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
        <span v-if="item.description" class="text-xs text-muted-foreground">{{ item.description }}</span>
      </ContextMenuItem>
      <p v-if="items.length === 0" class="px-2 py-2 text-xs text-muted-foreground">{{ emptyLabel }}</p>
    </ContextMenuSubContent>
  </ContextMenuSub>
</template>
